import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiHeart,
  FiMinus,
  FiPlus,
  FiShare2,
} from "react-icons/fi";
import productData, { siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";
import { ProductCard } from "./Shop";
import "../css/productdetails.scss";

const sampleReviews = [
  ["Avery", "The cloth feels substantial without being stiff, and the measurements were right on."],
  ["Cameron", "Excellent construction and a fit that makes sense from the first wear."],
  ["Reese", "The details are quiet but thoughtful. This is the pair I keep reaching for."],
];

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, wishlistItems, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [optionError, setOptionError] = useState("");
  const [shareStatus, setShareStatus] = useState("");
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    if (!product) return;
    setSelectedSize("");
    setSelectedColor(product.colors[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setOptionError("");
    setImageFailed(false);
    document.title = `${product.name} | ${siteConfig.brandName}`;
  }, [product]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const sameCategory = productData.filter(
      (item) => item.id !== product.id && item.category === product.category
    );
    const otherCategories = productData.filter(
      (item) => item.id !== product.id && item.category !== product.category
    );
    return [...sameCategory, ...otherCategories].slice(0, 3);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found">
        <span className="eyebrow">404 — Product not found</span>
        <h1>This piece is no longer in the collection.</h1>
        <Link className="button button--accent" to="/shop">
          <FiArrowLeft aria-hidden="true" /> Back to shop
        </Link>
      </main>
    );
  }

  const isWishlisted = wishlistItems.includes(product.id);

  const handleAddToCart = () => {
    if (!selectedSize) {
      setOptionError("Choose a size before adding this item to your cart.");
      return;
    }

    addToCart(product.id, quantity, selectedSize, selectedColor);
    navigate("/cartpage");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShare = async () => {
    const shareData = { title: product.name, url: window.location.href };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(window.location.href);
        setShareStatus("Link copied");
        window.setTimeout(() => setShareStatus(""), 2000);
      }
    } catch {
      setShareStatus("");
    }
  };

  return (
    <main className="product-detail">
      <nav className="product-detail__breadcrumb page-width" aria-label="Breadcrumb">
        <Link to="/">Home</Link><span>/</span>
        <Link to={`/shop?category=${product.category}`}>{product.category}</Link><span>/</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <div className="product-detail__layout page-width">
        <section
          className={`product-detail__gallery${product.images.length > 1 ? "" : " product-detail__gallery--single"}`}
          aria-label="Product images"
        >
          {product.images.length > 1 && (
            <div className="product-detail__thumbs">
              {product.images.map((image, index) => (
                <button
                  key={image}
                  type="button"
                  className={selectedImage === index ? "is-active" : ""}
                  onClick={() => {
                    setSelectedImage(index);
                    setImageFailed(false);
                  }}
                  aria-label={`Show image ${index + 1}`}
                >
                  <img src={image} alt="" width="1086" height="1448" />
                </button>
              ))}
            </div>
          )}
          <div className="product-detail__main-image">
            {product.badge && <span>{product.badge}</span>}
            {imageFailed ? (
              <div className="product-detail__image-fallback">{product.category}</div>
            ) : (
              <img
                src={product.images[selectedImage] || product.image}
                alt={product.name}
                width="1086"
                height="1448"
                onError={() => setImageFailed(true)}
              />
            )}
          </div>
        </section>

        <section className="product-detail__info">
          <span className="eyebrow">{product.category} · {product.attributes.Fit}</span>
          <h1>{product.name}</h1>
          <div className="product-detail__rating">
            <span aria-label={`${product.rating} out of 5 stars`}>★★★★★</span>
            <strong>{product.rating.toFixed(1)}</strong>
            <a href="#reviews">Reviews {product.reviews}</a>
          </div>

          <p className="product-detail__intro">{product.description}</p>

          <div className="product-detail__options">
            <div>
              <label htmlFor="product-color">Color</label>
              <select
                id="product-color"
                value={selectedColor}
                onChange={(event) => setSelectedColor(event.target.value)}
              >
                {product.colors.map((color) => <option key={color}>{color}</option>)}
              </select>
            </div>
            <div>
              <div className="product-detail__size-label">
                <label htmlFor="product-size">Size</label>
                <span>Fit guide</span>
              </div>
              <select
                id="product-size"
                value={selectedSize}
                onChange={(event) => {
                  setSelectedSize(event.target.value);
                  setOptionError("");
                }}
                aria-invalid={Boolean(optionError)}
              >
                <option value="">Choose options</option>
                {product.sizes.map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
              {optionError && <p className="form-error" role="alert">{optionError}</p>}
            </div>
          </div>

          <div className="product-detail__quantity">
            <span>Quantity</span>
            <div>
              <button type="button" onClick={() => setQuantity((current) => Math.max(1, current - 1))} aria-label="Decrease quantity">
                <FiMinus aria-hidden="true" />
              </button>
              <span aria-live="polite">{quantity}</span>
              <button type="button" onClick={() => setQuantity((current) => current + 1)} aria-label="Increase quantity">
                <FiPlus aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="product-detail__buy-box">
            <div>
              <small>Price</small>
              <strong>{siteConfig.currencySymbol}{product.price.toFixed(2)}</strong>
              {product.compareAtPrice && <del>{siteConfig.currencySymbol}{product.compareAtPrice.toFixed(2)}</del>}
            </div>
            <button className="button button--accent" type="button" onClick={handleAddToCart}>
              Add to cart <FiArrowRight aria-hidden="true" />
            </button>
          </div>

          <div className="product-detail__secondary-actions">
            <button type="button" className={isWishlisted ? "is-active" : ""} onClick={() => toggleWishlist(product.id)}>
              <FiHeart aria-hidden="true" /> {isWishlisted ? "Saved to wishlist" : "Add to wishlist"}
            </button>
            <button type="button" onClick={handleShare}>
              <FiShare2 aria-hidden="true" /> {shareStatus || "Share"}
            </button>
          </div>

          <div className="product-detail__details">
            <h2>Details</h2>
            <ul>
              {product.details.map((detail) => <li key={detail}><FiCheck aria-hidden="true" /> {detail}</li>)}
            </ul>
            <dl>
              {Object.entries(product.attributes).map(([label, value]) => (
                <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
              ))}
              <div><dt>Material</dt><dd>{product.material}</dd></div>
            </dl>
          </div>

          <div className="product-detail__shipping-note">
            <h2>Shipping &amp; returns</h2>
            <p>Free U.S. shipping and returns over ${siteConfig.freeShippingThreshold}. Unworn full-price pieces may be returned within 30 days.</p>
            <Link to="/shipping">Read the full policy <FiArrowRight aria-hidden="true" /></Link>
          </div>
        </section>
      </div>

      <section className="product-detail__reviews page-width" id="reviews" aria-labelledby="reviews-heading">
        <div className="section-heading">
          <span className="eyebrow">Field notes</span>
          <h2 id="reviews-heading">Reviews</h2>
        </div>
        <div className="product-detail__review-grid">
          {sampleReviews.map(([name, review]) => (
            <article key={name}>
              <div><strong>{name}</strong><span>★★★★★</span></div>
              <p>{review}</p>
              <small>Verified buyer</small>
            </article>
          ))}
        </div>
      </section>

      <section className="product-detail__related page-width" aria-labelledby="related-heading">
        <div className="section-heading section-heading--center">
          <span className="eyebrow">Wear it with</span>
          <h2 id="related-heading">Complete the look</h2>
        </div>
        <div className="product-detail__related-grid">
          {relatedProducts.map((item) => <ProductCard key={item.id} product={item} compact />)}
        </div>
      </section>
    </main>
  );
}

export default ProductDetail;
